import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EventAvailable as ExtendAccessIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  EmojiEvents as MedalIcon,
  Speed as SpeedIcon,
  CheckCircleOutline as RunningIcon,
  CancelOutlined as ExpiredIcon,
  WarningAmber as ExpiringSoonIcon,
  AllInclusive as UnlimitedIcon,
  School as SchoolIcon,
  Groups as MultiCourseIcon,
  PersonAddAlt1 as NewEnrollmentIcon,
  CalendarMonth as YearEnrollmentIcon,
  CurrencyRupee as FeeIcon,
  AccountBalanceWallet as DueIcon,
  Visibility as RevealIcon,
  VisibilityOff as HideIcon,
  KeyboardArrowUp as DockExpandIcon,
  KeyboardArrowDown as DockCollapseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getApiBaseUrl, getAuthHeader } from '../../services/httpClient';

const API_BASE_URL = getApiBaseUrl();
const EXPIRING_SOON_DAYS = 7;

const formatINR = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const parseJoiningDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isJoinedThisMonth = (report, now = new Date()) => {
  const joined = parseJoiningDate(report?.joiningDate);
  if (!joined) return false;
  return joined.getFullYear() === now.getFullYear() && joined.getMonth() === now.getMonth();
};

const isJoinedThisYear = (report, now = new Date()) => {
  const joined = parseJoiningDate(report?.joiningDate);
  if (!joined) return false;
  return joined.getFullYear() === now.getFullYear();
};

const METRIC_COLUMNS = [
  { key: 'videos', label: 'Videos', color: '#6D28D9', sortKey: 'videos' },
  { key: 'flashcards', label: 'Flash Cards', color: '#10B981', sortKey: 'flashcards' },
  { key: 'notes', label: 'Notes', color: '#2563EB', sortKey: 'notes' },
  { key: 'quizzes', label: 'Quizzes', color: '#F97316', sortKey: 'quizzes' },
  { key: 'assignments', label: 'Assignments', color: '#0EA5E9', sortKey: 'assignments' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatLeftDays = (value, isExpired) => {
  if (value == null) return 'Unlimited';
  if (isExpired || value <= 0) return 'Expired';
  return `${value} day${value === 1 ? '' : 's'}`;
};

const isReportExpired = (report) =>
  Boolean(report?.isExpired) || (report?.leftDays != null && report.leftDays <= 0);

const isReportRunning = (report) => !isReportExpired(report);

const isReportExpiringSoon = (report) =>
  isReportRunning(report) &&
  report?.leftDays != null &&
  report.leftDays > 0 &&
  report.leftDays <= EXPIRING_SOON_DAYS;

const isReportUnlimited = (report) =>
  isReportRunning(report) && report?.leftDays == null;

const buildAccessStats = (reportsList) => {
  const now = new Date();
  let running = 0;
  let expired = 0;
  let unlimited = 0;
  let expiringSoon = 0;
  let multiCourse = 0;
  let enrollmentSlots = 0;
  let newThisMonth = 0;
  let enrolledThisYear = 0;
  let dueCount = 0;
  let totalDue = 0;
  let totalFee = 0;
  let totalPaid = 0;
  const courseMap = new Map();

  const bumpCourse = (code, name, { expired: courseExpired = false } = {}) => {
    const key = String(code || 'UNKNOWN').toUpperCase();
    if (!courseMap.has(key)) {
      courseMap.set(key, {
        code: key,
        name: name || key,
        total: 0,
        running: 0,
        expired: 0,
      });
    }
    const entry = courseMap.get(key);
    if (name && entry.name === entry.code) entry.name = name;
    entry.total += 1;
    if (courseExpired) entry.expired += 1;
    else entry.running += 1;
  };

  for (const report of reportsList) {
    const enrolled = report.enrolledCourses || [];
    const names = report.courseNames || [];
    const accessList = report.courseAccess || [];
    const dueFee = Number(report.dueFee) || 0;
    const fee = Number(report.totalFee) || 0;
    const paid = Number(report.paidFee) || 0;

    totalFee += fee;
    totalPaid += paid;
    if (dueFee > 0) {
      dueCount += 1;
      totalDue += dueFee;
    }

    if (enrolled.length > 1 || names.length > 1 || accessList.length > 1) {
      multiCourse += 1;
    }

    if (isJoinedThisMonth(report, now)) newThisMonth += 1;
    if (isJoinedThisYear(report, now)) enrolledThisYear += 1;

    if (isReportUnlimited(report)) unlimited += 1;
    if (isReportExpiringSoon(report)) expiringSoon += 1;
    if (isReportExpired(report)) expired += 1;
    else running += 1;

    if (accessList.length > 0) {
      accessList.forEach((access) => {
        enrollmentSlots += 1;
        bumpCourse(access.course_code, access.course_name, {
          expired: access.is_expired === true || (access.days_remaining != null && access.days_remaining <= 0),
        });
      });
    } else if (enrolled.length > 0) {
      enrolled.forEach((code, index) => {
        enrollmentSlots += 1;
        bumpCourse(code, names[index], { expired: isReportExpired(report) });
      });
    } else if (names.length > 0) {
      names.forEach((name) => {
        enrollmentSlots += 1;
        bumpCourse(name, name, { expired: isReportExpired(report) });
      });
    }
  }

  const courses = [...courseMap.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  return {
    running,
    expired,
    unlimited,
    expiringSoon,
    multiCourse,
    enrollmentSlots,
    newThisMonth,
    enrolledThisYear,
    dueCount,
    totalDue: Math.round(totalDue * 100) / 100,
    totalFee: Math.round(totalFee * 100) / 100,
    totalPaid: Math.round(totalPaid * 100) / 100,
    monthLabel: now.toLocaleString('en-IN', { month: 'short', year: 'numeric' }),
    yearLabel: String(now.getFullYear()),
    courses,
  };
};

const ProgressCell = ({ value, color, compact = false }) => (
  <Box sx={{ minWidth: compact ? 64 : 88 }}>
    <Typography variant="body2" fontWeight={700} sx={{ color, mb: 0.5, fontSize: compact ? '0.8rem' : undefined }}>
      {value}%
    </Typography>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, value))}
      sx={{
        height: compact ? 5 : 6,
        borderRadius: 999,
        bgcolor: `${color}22`,
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 999 },
      }}
    />
  </Box>
);

const LeftDaysChip = ({ leftDays, isExpired }) => (
  <Chip
    size="small"
    label={formatLeftDays(leftDays, isExpired)}
    sx={{
      fontWeight: 700,
      bgcolor:
        isExpired || leftDays === 0
          ? '#FEE2E2'
          : leftDays == null
            ? '#ECFDF5'
            : '#FEF3C7',
      color:
        isExpired || leftDays === 0
          ? '#B91C1C'
          : leftDays == null
            ? '#047857'
            : '#B45309',
    }}
  />
);

const StudentReportMobileCard = ({ report, index, onExtend }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2.5,
      borderColor: '#E2E8F0',
      bgcolor: index % 2 === 0 ? '#fff' : '#FAFBFC',
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            #{index + 1}
          </Typography>
          <Typography fontWeight={800} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
            {report.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            ID: {report.studentId || '—'} · {report.phone || '—'}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.75}>
          <LeftDaysChip leftDays={report.leftDays} isExpired={report.isExpired} />
          <Chip
            icon={<MedalIcon sx={{ fontSize: '14px !important' }} />}
            label={report.medal?.label || 'Bronze Learner'}
            size="small"
            sx={{
              fontWeight: 700,
              maxWidth: 140,
              bgcolor: `${report.medal?.color || '#B45309'}18`,
              color: report.medal?.color || '#B45309',
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        </Stack>
      </Stack>

      <Typography variant="body2" sx={{ mt: 1.25, color: '#334155' }}>
        {(report.courseNames || []).join(', ') || '—'}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Joined {formatDate(report.joiningDate)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Complete {formatDate(report.courseCompletionDate)}
        </Typography>
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          Overall
        </Typography>
        <ProgressCell value={report.overall || 0} color="#1E3A78" compact />
      </Box>

      <Grid container spacing={1.25} sx={{ mt: 0.5 }}>
        {METRIC_COLUMNS.map((column) => (
          <Grid item xs={6} key={column.key}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {column.label}
            </Typography>
            <ProgressCell
              value={report.metrics?.[column.key] || 0}
              color={column.color}
              compact
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <SpeedIcon sx={{ fontSize: 15, color: '#7C3AED' }} />
            <Typography variant="caption" fontWeight={700}>
              {report.ttSpeed || 0} WPM
            </Typography>
          </Stack>
          <Typography variant="caption" fontWeight={700}>
            {report.ttAccuracy || 0}% acc
          </Typography>
          <Typography variant="caption" fontWeight={700}>
            {report.dailyStreak || 0}d streak
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ExtendAccessIcon />}
          onClick={() => onExtend(report)}
          disabled={!(report.enrolledCourses || []).length}
          sx={{ textTransform: 'none' }}
        >
          Extend
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

const PrivacyFinanceChip = ({ label, revealedValue, hint, icon, color, bg }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const show = hovered || pinned;

  return (
    <Tooltip title={show ? hint : 'Hover or tap to reveal (privacy protected)'}>
      <Chip
        clickable
        icon={show ? icon : <HideIcon sx={{ fontSize: 18 }} />}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => {
          if (!pinned) setHovered(false);
        }}
        onClick={() => setPinned((current) => !current)}
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
            <Box
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: '0.95rem',
                letterSpacing: show ? 0 : '0.12em',
                filter: show ? 'none' : 'blur(5px)',
                userSelect: show ? 'auto' : 'none',
                transition: 'filter 0.15s ease',
                minWidth: show ? 'auto' : 52,
              }}
            >
              {show ? revealedValue : '••••••'}
            </Box>
            <Box component="span" sx={{ opacity: 0.85 }}>
              {label}
            </Box>
            {pinned ? <RevealIcon sx={{ fontSize: 14, ml: 0.25, opacity: 0.85 }} /> : null}
          </Box>
        }
        sx={{
          height: 36,
          fontWeight: 700,
          bgcolor: show ? color : bg,
          color: show ? '#fff' : color,
          border: `1px dashed ${color}66`,
          '& .MuiChip-icon': { color: show ? '#fff' : color },
          '&:hover': {
            bgcolor: show ? color : `${color}22`,
          },
        }}
      />
    </Tooltip>
  );
};

const StudentReportsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [sortBy, setSortBy] = useState('performance');
  const [sortOrder, setSortOrder] = useState('desc');
  const [extendDialog, setExtendDialog] = useState(null);
  const [extendCourseCode, setExtendCourseCode] = useState('');
  const [extendDays, setExtendDays] = useState('10');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statsDockOpen, setStatsDockOpen] = useState(false);
  const statsHideTimerRef = useRef(null);
  const statsDockSuppressOpenRef = useRef(false);

  const clearStatsHideTimer = () => {
    if (statsHideTimerRef.current) {
      clearTimeout(statsHideTimerRef.current);
      statsHideTimerRef.current = null;
    }
  };

  const openStatsDock = () => {
    if (statsDockSuppressOpenRef.current) return;
    clearStatsHideTimer();
    setStatsDockOpen(true);
  };

  const scheduleStatsDockHide = () => {
    clearStatsHideTimer();
    statsHideTimerRef.current = setTimeout(() => {
      setStatsDockOpen(false);
      statsHideTimerRef.current = null;
    }, 380);
  };

  const toggleStatsDock = () => {
    clearStatsHideTimer();
    setStatsDockOpen((current) => {
      if (current) {
        statsDockSuppressOpenRef.current = true;
        return false;
      }
      statsDockSuppressOpenRef.current = false;
      return true;
    });
  };

  useEffect(() => () => clearStatsHideTimer(), []);

  const { data: coursesData } = useQuery('student-reports-courses', async () => {
    const response = await axios.get(`${API_BASE_URL}/students/courses/list`);
    return response.data;
  });

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(
    ['student-performance-reports', search, courseFilter, sortBy, sortOrder],
    async () => {
      const params = new URLSearchParams({
        sortBy,
        sortOrder,
      });
      if (search) params.set('search', search);
      if (courseFilter && courseFilter !== 'all') params.set('course', courseFilter);

      const response = await axios.get(
        `${API_BASE_URL}/students/reports/performance?${params.toString()}`,
        { headers: getAuthHeader() },
      );
      return response.data;
    },
    { keepPreviousData: true },
  );

  const extendCourseMutation = useMutation(
    async ({ studentId, courseCode, days }) => {
      const response = await axios.post(
        `${API_BASE_URL}/students/${studentId}/courses/${encodeURIComponent(courseCode)}/extend-access`,
        { days: Number(days) },
        { headers: getAuthHeader() },
      );
      return response.data;
    },
    {
      onSuccess: (responseData) => {
        toast.success(responseData?.message || 'Course access extended');
        setExtendDialog(null);
        setExtendCourseCode('');
        setExtendDays('10');
        queryClient.invalidateQueries(['student-performance-reports']);
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message || err?.message || 'Failed to extend course access',
        );
      },
    },
  );

  const reports = data?.reports || [];
  const summary = data?.summary || {};
  const courses = coursesData?.courses || [];

  const accessStats = useMemo(() => buildAccessStats(reports), [reports]);

  const displayedReports = useMemo(() => {
    if (statusFilter === 'running') return reports.filter(isReportRunning);
    if (statusFilter === 'expired') return reports.filter(isReportExpired);
    if (statusFilter === 'expiring_soon') return reports.filter(isReportExpiringSoon);
    if (statusFilter === 'unlimited') return reports.filter(isReportUnlimited);
    if (statusFilter === 'this_month') return reports.filter((report) => isJoinedThisMonth(report));
    if (statusFilter === 'this_year') return reports.filter((report) => isJoinedThisYear(report));
    if (statusFilter === 'due') return reports.filter((report) => (Number(report.dueFee) || 0) > 0);
    if (statusFilter === 'multi_course') {
      return reports.filter((report) => {
        const count = Math.max(
          (report.enrolledCourses || []).length,
          (report.courseNames || []).length,
          (report.courseAccess || []).length,
        );
        return count > 1;
      });
    }
    return reports;
  }, [reports, statusFilter]);

  const topPerformer = useMemo(() => {
    if (!reports.length) return null;
    return [...reports].sort((a, b) => (b.overall || 0) - (a.overall || 0))[0];
  }, [reports]);

  const toggleStatusFilter = (next) => {
    setStatusFilter((current) => (current === next ? 'all' : next));
  };

  const handleCourseChipClick = (courseCode) => {
    const normalized = String(courseCode || '').toUpperCase();
    const match = courses.find(
      (course) => String(course.course_code || '').toUpperCase() === normalized,
    );
    const nextValue = match?.course_code || courseCode;
    setCourseFilter((current) =>
      String(current || '').toUpperCase() === normalized ? 'all' : nextValue,
    );
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnKey);
    const defaultDesc = ![
      'name',
      'student',
      'course',
      'rank',
      'joining_date',
      'phone',
      'student_id',
    ].includes(columnKey);
    setSortOrder(defaultDesc ? 'desc' : 'asc');
  };

  const openExtendDialog = (report) => {
    const enrolled = report.enrolledCourses || [];
    setExtendDialog(report);
    setExtendCourseCode(enrolled[0] || '');
    setExtendDays('10');
  };

  const handleExtendCourse = () => {
    if (!extendDialog?.id || !extendCourseCode) {
      toast.error('Select a course to extend');
      return;
    }
    const days = Number(extendDays);
    if (!Number.isFinite(days) || days < 1) {
      toast.error('Enter a valid number of days');
      return;
    }
    extendCourseMutation.mutate({
      studentId: extendDialog.id,
      courseCode: extendCourseCode,
      days,
    });
  };

  const SortableHeader = ({ label, columnKey, sx = {} }) => (
    <TableCell sx={{ fontWeight: 800, bgcolor: '#F8FAFC', ...sx }}>
      <TableSortLabel
        active={sortBy === columnKey}
        direction={sortBy === columnKey ? sortOrder : 'asc'}
        onClick={() => handleSort(columnKey)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const selectedAccess = (extendDialog?.courseAccess || []).find(
    (item) =>
      String(item.course_code || '').toUpperCase() ===
      String(extendCourseCode || '').toUpperCase(),
  );

  return (
    <Box
      sx={{
        pb: { xs: 8, md: 6 },
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      <Card
        sx={{
          mb: { xs: 2, md: 3 },
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A78 55%, #2563EB 100%)',
          color: '#fff',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard/students')}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ color: '#fff', textTransform: 'none', px: { xs: 0, sm: 1 } }}
                >
                  {isMobile ? 'Students' : 'Students Management'}
                </Button>
                <Tooltip title="Refresh reports">
                  <IconButton
                    onClick={() => refetch()}
                    sx={{ color: '#fff', display: { xs: 'inline-flex', md: 'none' } }}
                    disabled={isFetching}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                fontWeight={800}
                sx={{ lineHeight: 1.2 }}
              >
                Student Reports
              </Typography>
              <Typography sx={{ opacity: 0.9, mt: 0.5, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                {isMobile
                  ? 'Track progress, access days, and extend courses.'
                  : 'Single-page performance view — sort any column, track access dates, and extend courses.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Tooltip title="Refresh reports">
                <IconButton onClick={() => refetch()} sx={{ color: '#fff' }} disabled={isFetching}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={{ xs: 1.25, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {[
          { label: 'Total Students', value: summary.totalStudents ?? 0, color: '#1E3A78' },
          { label: 'Active Learners', value: summary.activeStudents ?? 0, color: '#059669' },
          { label: 'Average Progress', value: `${summary.averageOverall ?? 0}%`, color: '#7C3AED' },
          {
            label: 'Top Performer',
            value: topPerformer?.name || '—',
            sub: topPerformer ? `${topPerformer.overall}%` : '',
            color: '#D97706',
          },
        ].map((item) => (
          <Grid item xs={6} md={3} key={item.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  {item.label}
                </Typography>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  fontWeight={800}
                  sx={{
                    color: item.color,
                    mt: 0.5,
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    fontSize: { xs: '1.05rem', sm: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {item.value}
                </Typography>
                {item.sub ? (
                  <Typography variant="caption" color="text.secondary">
                    {item.sub}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              sx={{ flex: 1, width: '100%', maxWidth: { lg: 420 } }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={isMobile ? 'Search name, phone, ID...' : 'Search name, phone, student ID...'}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>Course</InputLabel>
              <Select
                label="Course"
                value={courseFilter}
                onChange={(event) => setCourseFilter(event.target.value)}
              >
                <MenuItem value="all">All Courses</MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course.course_code} value={course.course_code}>
                    {course.course_name || course.course_code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isMobile ? (
              <FormControl size="small" sx={{ minWidth: '100%', width: '100%' }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={`${sortBy}:${sortOrder}`}
                  onChange={(event) => {
                    const [nextSortBy, nextSortOrder] = String(event.target.value).split(':');
                    setSortBy(nextSortBy);
                    setSortOrder(nextSortOrder === 'asc' ? 'asc' : 'desc');
                  }}
                >
                  <MenuItem value="performance:desc">Overall · High to low</MenuItem>
                  <MenuItem value="performance:asc">Overall · Low to high</MenuItem>
                  <MenuItem value="left_days:asc">Left days · Soonest</MenuItem>
                  <MenuItem value="left_days:desc">Left days · Longest</MenuItem>
                  <MenuItem value="joining_date:desc">Joining · Newest</MenuItem>
                  <MenuItem value="joining_date:asc">Joining · Oldest</MenuItem>
                  <MenuItem value="name:asc">Name · A–Z</MenuItem>
                  <MenuItem value="name:desc">Name · Z–A</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}
              >
                Click any column header to sort
              </Typography>
            )}
          </Stack>
          {(statusFilter !== 'all' || courseFilter !== 'all') && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {statusFilter !== 'all' ? (
                <Chip
                  size="small"
                  color="primary"
                  label={`Status: ${String(statusFilter).replace(/_/g, ' ')}`}
                  onDelete={() => setStatusFilter('all')}
                />
              ) : null}
              {courseFilter !== 'all' ? (
                <Chip
                  size="small"
                  color="secondary"
                  label={`Course: ${courseFilter}`}
                  onDelete={() => setCourseFilter('all')}
                />
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Showing {displayedReports.length} of {reports.length} students
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      {data?.generatedAt ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Real stats from {data.dataSource || 'lms_student_progress'}. Updated:{' '}
          {new Date(data.generatedAt).toLocaleString('en-IN')}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.response?.data?.message || error?.message || 'Failed to load student reports'}
        </Alert>
      ) : null}

      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Stack spacing={1.25}>
              {displayedReports.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">No students found for this filter.</Typography>
                </Box>
              ) : (
                displayedReports.map((report, index) => (
                  <StudentReportMobileCard
                    key={report.id}
                    report={report}
                    index={index}
                    onExtend={openExtendDialog}
                  />
                ))
              )}
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: '72vh',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 1280 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#F8FAFC' }}>#</TableCell>
                    <SortableHeader label="Student" columnKey="name" sx={{ minWidth: 180 }} />
                    <SortableHeader label="Course" columnKey="course" />
                    <SortableHeader label="Joining Date" columnKey="joining_date" sx={{ minWidth: 120 }} />
                    <SortableHeader
                      label="Course Completion"
                      columnKey="course_completion_date"
                      sx={{ minWidth: 130 }}
                    />
                    <SortableHeader label="Left Days" columnKey="left_days" sx={{ minWidth: 100 }} />
                    <SortableHeader label="Overall" columnKey="overall" sx={{ minWidth: 120 }} />
                    {METRIC_COLUMNS.map((column) => (
                      <SortableHeader
                        key={column.key}
                        label={column.label}
                        columnKey={column.sortKey}
                        sx={{ minWidth: 110 }}
                      />
                    ))}
                    <SortableHeader label="TT Speed" columnKey="tt_speed" />
                    <SortableHeader label="Accuracy" columnKey="accuracy" />
                    <SortableHeader label="Streak" columnKey="streak" />
                    <SortableHeader label="Rank" columnKey="rank" />
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#F8FAFC', minWidth: 110 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedReports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No students found for this filter.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedReports.map((report, index) => (
                      <TableRow
                        key={report.id}
                        hover
                        sx={{
                          '&:nth-of-type(even)': { bgcolor: '#FAFBFC' },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight={700}>{report.name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ID: {report.studentId || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {report.phone || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(report.courseNames || []).slice(0, 2).join(', ') || '—'}
                          </Typography>
                          {(report.courseNames || []).length > 2 ? (
                            <Typography variant="caption" color="text.secondary">
                              +{(report.courseNames || []).length - 2} more
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(report.joiningDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(report.courseCompletionDate)}
                          </Typography>
                          {report.certificateIssued ? (
                            <Typography variant="caption" color="success.main" display="block">
                              Certificate issued
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <LeftDaysChip leftDays={report.leftDays} isExpired={report.isExpired} />
                        </TableCell>
                        <TableCell>
                          <ProgressCell value={report.overall || 0} color="#1E3A78" />
                        </TableCell>
                        {METRIC_COLUMNS.map((column) => (
                          <TableCell key={column.key}>
                            <ProgressCell
                              value={report.metrics?.[column.key] || 0}
                              color={column.color}
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <SpeedIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                            <Typography variant="body2" fontWeight={700}>
                              {report.ttSpeed || 0} WPM
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {report.ttAccuracy || 0}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {report.dailyStreak || 0} days
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<MedalIcon sx={{ fontSize: '16px !important' }} />}
                            label={report.medal?.label || 'Bronze Learner'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: `${report.medal?.color || '#B45309'}18`,
                              color: report.medal?.color || '#B45309',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Extend course access">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ExtendAccessIcon />}
                                onClick={() => openExtendDialog(report)}
                                disabled={!(report.enrolledCourses || []).length}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                              >
                                Extend
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {isFetching && !isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : null}

      <Box
        sx={{
          position: 'fixed',
          left: { xs: 0, md: 240 },
          right: 0,
          bottom: 0,
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pointerEvents: 'none',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <Box
          onMouseEnter={!isMobile ? openStatsDock : undefined}
          onMouseLeave={
            !isMobile
              ? () => {
                  statsDockSuppressOpenRef.current = false;
                  scheduleStatsDockHide();
                }
              : undefined
          }
          sx={{
            pointerEvents: 'auto',
            width: statsDockOpen ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
        <Tooltip
          title={
            statsDockOpen
              ? isMobile
                ? 'Tap to hide insights'
                : 'Click to hide · moves away auto-hides'
              : 'Show live student insights'
          }
          placement="top"
        >
          <Box
            component="button"
            type="button"
            aria-label={statsDockOpen ? 'Hide insights dock' : 'Show insights dock'}
            aria-expanded={statsDockOpen}
            onClick={toggleStatsDock}
            sx={{
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              minWidth: { xs: 56, sm: 72 },
              height: { xs: 34, sm: 30 },
              px: 1.5,
              mb: statsDockOpen ? '-1px' : 0,
              borderRadius: '14px 14px 0 0',
              bgcolor: '#0F172A',
              color: '#fff',
              boxShadow: '0 -4px 18px rgba(15, 23, 42, 0.28)',
              transition: 'transform 0.2s ease, background-color 0.2s ease',
              touchAction: 'manipulation',
              '&:hover': {
                bgcolor: '#1E3A78',
                transform: { xs: 'none', md: 'translateY(-2px)' },
              },
              '&:focus-visible': {
                outline: '2px solid #60A5FA',
                outlineOffset: 2,
              },
            }}
          >
            {statsDockOpen ? (
              <DockCollapseIcon sx={{ fontSize: 22 }} />
            ) : (
              <DockExpandIcon sx={{ fontSize: 22 }} />
            )}
            {!statsDockOpen ? (
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  opacity: 0.92,
                  display: { xs: 'none', sm: 'inline' },
                }}
              >
                Insights
              </Typography>
            ) : null}
          </Box>
        </Tooltip>

        <Collapse
          in={statsDockOpen}
          timeout={260}
          sx={{ width: '100%' }}
        >
          <Paper
            elevation={10}
            sx={{
              width: '100%',
              borderRadius: { xs: '16px 16px 0 0', md: '18px 18px 0 0' },
              borderTop: '1px solid',
              borderColor: 'rgba(30, 58, 120, 0.12)',
              bgcolor: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(10px)',
              px: { xs: 1.25, md: 2.5 },
              py: { xs: 1.25, md: 1.5 },
              boxShadow: '0 -8px 28px rgba(15, 23, 42, 0.12)',
              maxHeight: { xs: '48vh', sm: '42vh', md: 'none' },
              overflowY: { xs: 'auto', md: 'visible' },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[
                    {
                      key: 'running',
                      label: 'Running',
                      value: accessStats.running,
                      hint: 'Not expired',
                      icon: <RunningIcon sx={{ fontSize: 18 }} />,
                      color: '#059669',
                      bg: '#ECFDF5',
                    },
                    {
                      key: 'expired',
                      label: 'Expired',
                      value: accessStats.expired,
                      hint: 'Access ended',
                      icon: <ExpiredIcon sx={{ fontSize: 18 }} />,
                      color: '#B91C1C',
                      bg: '#FEE2E2',
                    },
                    {
                      key: 'expiring_soon',
                      label: 'Expiring ≤7d',
                      value: accessStats.expiringSoon,
                      hint: 'Needs attention',
                      icon: <ExpiringSoonIcon sx={{ fontSize: 18 }} />,
                      color: '#B45309',
                      bg: '#FEF3C7',
                    },
                    {
                      key: 'unlimited',
                      label: 'Unlimited',
                      value: accessStats.unlimited,
                      hint: 'No expiry',
                      icon: <UnlimitedIcon sx={{ fontSize: 18 }} />,
                      color: '#1D4ED8',
                      bg: '#DBEAFE',
                    },
                    {
                      key: 'multi_course',
                      label: 'Multi-course',
                      value: accessStats.multiCourse,
                      hint: '2+ enrollments',
                      icon: <MultiCourseIcon sx={{ fontSize: 18 }} />,
                      color: '#7C3AED',
                      bg: '#F3E8FF',
                    },
                    {
                      key: 'this_month',
                      label: `New ${accessStats.monthLabel || 'month'}`,
                      value: accessStats.newThisMonth,
                      hint: 'Joined this month',
                      icon: <NewEnrollmentIcon sx={{ fontSize: 18 }} />,
                      color: '#0F766E',
                      bg: '#CCFBF1',
                    },
                    {
                      key: 'this_year',
                      label: `${accessStats.yearLabel || 'Year'} total`,
                      value: accessStats.enrolledThisYear,
                      hint: 'Joined in the current year',
                      icon: <YearEnrollmentIcon sx={{ fontSize: 18 }} />,
                      color: '#0369A1',
                      bg: '#E0F2FE',
                    },
                  ].map((item) => {
                    const active = statusFilter === item.key;
                    return (
                      <Tooltip key={item.key} title={`Filter: ${item.hint}. Click again to clear.`}>
                        <Chip
                          clickable
                          icon={item.icon}
                          onClick={() => toggleStatusFilter(item.key)}
                          label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
                              <Box component="span" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                                {item.value}
                              </Box>
                              <Box component="span" sx={{ opacity: 0.85 }}>
                                {item.label}
                              </Box>
                            </Box>
                          }
                          sx={{
                            height: 36,
                            fontWeight: 700,
                            bgcolor: active ? item.color : item.bg,
                            color: active ? '#fff' : item.color,
                            border: `1px solid ${item.color}33`,
                            '& .MuiChip-icon': { color: active ? '#fff' : item.color },
                            '&:hover': {
                              bgcolor: active ? item.color : `${item.color}22`,
                            },
                          }}
                        />
                      </Tooltip>
                    );
                  })}

                  <PrivacyFinanceChip
                    label="Due"
                    revealedValue={`${accessStats.dueCount} · ${formatINR(accessStats.totalDue)}`}
                    hint={`${accessStats.dueCount} students with pending due`}
                    icon={<DueIcon sx={{ fontSize: 18 }} />}
                    color="#BE123C"
                    bg="#FFE4E6"
                  />
                  <PrivacyFinanceChip
                    label="Total fee"
                    revealedValue={formatINR(accessStats.totalFee)}
                    hint={`Collected ${formatINR(accessStats.totalPaid)} · Due ${formatINR(accessStats.totalDue)}`}
                    icon={<FeeIcon sx={{ fontSize: 18 }} />}
                    color="#334155"
                    bg="#F1F5F9"
                  />
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    whiteSpace: { xs: 'normal', md: 'nowrap' },
                    width: { xs: '100%', md: 'auto' },
                    textAlign: { xs: 'left', md: 'right' },
                  }}
                >
                  {accessStats.enrollmentSlots} course seats · {reports.length} students
                  {statusFilter !== 'all' ? ` · filtered ${displayedReports.length}` : ''}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  overflowX: 'auto',
                  pb: 0.25,
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(15,23,42,0.2)', borderRadius: 999 },
                }}
              >
                <Chip
                  size="small"
                  icon={<SchoolIcon sx={{ fontSize: '16px !important' }} />}
                  label="Course-wise"
                  sx={{
                    flexShrink: 0,
                    fontWeight: 800,
                    bgcolor: '#EFF6FF',
                    color: '#1E3A78',
                  }}
                />
                {accessStats.courses.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No course enrollments in this view
                  </Typography>
                ) : (
                  accessStats.courses.map((course) => {
                    const active =
                      String(courseFilter || '').toUpperCase() === String(course.code || '').toUpperCase();
                    return (
                      <Tooltip
                        key={course.code}
                        title={`${course.name}: ${course.running} running, ${course.expired} expired. Click to filter.`}
                      >
                        <Chip
                          clickable
                          size="small"
                          onClick={() => handleCourseChipClick(course.code)}
                          label={`${course.name} · ${course.total} (${course.running} active / ${course.expired} expired)`}
                          sx={{
                            flexShrink: 0,
                            fontWeight: 700,
                            bgcolor: active ? '#1E3A78' : '#F8FAFC',
                            color: active ? '#fff' : '#1E3A78',
                            border: '1px solid',
                            borderColor: active ? '#1E3A78' : '#E2E8F0',
                            '&:hover': {
                              bgcolor: active ? '#1E3A78' : '#EEF2FF',
                            },
                          }}
                        />
                      </Tooltip>
                    );
                  })
                )}
              </Stack>
            </Stack>
          </Paper>
        </Collapse>
        </Box>
      </Box>

      <Dialog
        open={Boolean(extendDialog)}
        onClose={() => !extendCourseMutation.isLoading && setExtendDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Extend course access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Extend LMS access for <strong>{extendDialog?.name}</strong> from today (or add days if
            access is still active).
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Course</InputLabel>
            <Select
              label="Course"
              value={extendCourseCode}
              onChange={(event) => setExtendCourseCode(event.target.value)}
            >
              {(extendDialog?.enrolledCourses || []).map((code) => {
                const access = (extendDialog?.courseAccess || []).find(
                  (item) =>
                    String(item.course_code || '').toUpperCase() === String(code).toUpperCase(),
                );
                return (
                  <MenuItem key={code} value={code}>
                    {access?.course_name || code}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Days to extend"
            value={extendDays}
            onChange={(event) => setExtendDays(event.target.value)}
            inputProps={{ min: 1, max: 3650 }}
            sx={{ mb: 2 }}
          />
          {selectedAccess ? (
            <Alert severity={selectedAccess.is_expired ? 'warning' : 'info'}>
              Current expiry: {formatDate(selectedAccess.expires_at)} · Left:{' '}
              {formatLeftDays(selectedAccess.days_remaining, selectedAccess.is_expired)}
            </Alert>
          ) : (
            <Alert severity="info">
              Joining: {formatDate(extendDialog?.joiningDate)} · Completion:{' '}
              {formatDate(extendDialog?.courseCompletionDate)} · Left:{' '}
              {formatLeftDays(extendDialog?.leftDays, extendDialog?.isExpired)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog(null)} disabled={extendCourseMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExtendCourse}
            disabled={extendCourseMutation.isLoading || !extendCourseCode}
            startIcon={
              extendCourseMutation.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ExtendAccessIcon />
              )
            }
          >
            Extend access
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentReportsPage;
